import { initializeApp } from "firebase/app";
import { getFirestore } from "firebase/firestore";
import { APP_CONFIG, FIREBASE_CONFIG, STORAGE_KEYS } from "../constants/authConfig";
import { getStorageItem, migrateFromLocalStorage, removeStorageItem, setStorageItem } from "./officeStorageService";

const app = initializeApp(FIREBASE_CONFIG);
export const db = getFirestore(app);

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function normalizeEmail(email) {
  return String(email || "").trim().toLowerCase();
}

export function validateEmail(email) {
  const normalized = normalizeEmail(email);
  if (!normalized) {
    return { valid: false, message: "Please enter your email address." };
  }
  if (!EMAIL_PATTERN.test(normalized)) {
    return { valid: false, message: "Please enter a valid email address." };
  }
  return { valid: true, email: normalized };
}

export async function getStoredEmail() {
  await migrateFromLocalStorage(Object.values(STORAGE_KEYS));
  return getStorageItem(STORAGE_KEYS.email);
}

export async function setStoredEmail(email) {
  await setStorageItem(STORAGE_KEYS.email, normalizeEmail(email));
}

export async function getStoredTrialEndDate() {
  return getStorageItem(STORAGE_KEYS.trialEndDate);
}

export async function setStoredTrialEndDate(endDate) {
  if (endDate) {
    await setStorageItem(STORAGE_KEYS.trialEndDate, endDate);
  }
}

export async function getLastRegisteredEmail() {
  return getStorageItem(STORAGE_KEYS.lastRegisteredEmail);
}

export async function setLastRegisteredEmail(email) {
  await setStorageItem(STORAGE_KEYS.lastRegisteredEmail, normalizeEmail(email));
}

export async function getEmailChangeCount() {
  const value = parseInt((await getStorageItem(STORAGE_KEYS.emailChangeCount)) || "0", 10);
  return Number.isFinite(value) ? value : 0;
}

export async function setEmailChangeCount(count) {
  await setStorageItem(STORAGE_KEYS.emailChangeCount, String(count));
}

export async function getRemainingEmailChanges() {
  const used = await getEmailChangeCount();
  return Math.max(APP_CONFIG.maxEmailChanges - used, 0);
}

export async function canChangeEmail() {
  const remaining = await getRemainingEmailChanges();
  return {
    allowed: remaining > 0,
    remaining,
    max: APP_CONFIG.maxEmailChanges,
  };
}

export async function clearEmailSession() {
  await removeStorageItem(STORAGE_KEYS.email);
  await removeStorageItem(STORAGE_KEYS.trialEndDate);
  await removeStorageItem(STORAGE_KEYS.subscriptionCheckedForDate);
  await removeStorageItem(STORAGE_KEYS.subscriptionStatus);
}

export async function saveUserEmail(email, { isEmailChange = false } = {}) {
  const validation = validateEmail(email);
  if (!validation.valid) {
    throw new Error(validation.message);
  }

  const normalizedEmail = validation.email;
  const lastRegisteredEmail = await getLastRegisteredEmail();

  if (isEmailChange && lastRegisteredEmail && lastRegisteredEmail !== normalizedEmail) {
    const changeCount = await getEmailChangeCount();
    if (changeCount >= APP_CONFIG.maxEmailChanges) {
      throw new Error(
        `You have used all ${APP_CONFIG.maxEmailChanges} email changes. Contact ${APP_CONFIG.supportEmail} for help.`
      );
    }
    await setEmailChangeCount(changeCount + 1);
  }

  await setStoredEmail(normalizedEmail);
  await setLastRegisteredEmail(normalizedEmail);
  return normalizedEmail;
}
